import { WHATSAPP_MESSAGE_SOURCE as SOURCE, formatShortDate } from '../../utils/whatsapp';
import CountrySelect from '../CountrySelect';
import { ORDERED_WITHIN_OPTIONS, SOURCE_OPTIONS } from './campaignWizardState';
import CsvAudienceInput from './CsvAudienceInput';

const fieldClass = 'w-full px-3 py-2 text-base bg-card border border-input rounded-lg';

const EventPicker = ({ events, selected, onToggle }) => {
  if (events.loading || (!events.data && !events.error))
    return <p className="text-sm text-muted-foreground">Loading events...</p>;
  if (events.error) {
    return (
      <div className="space-y-2">
        <p className="text-sm text-red-700 dark:text-red-300">{events.error}</p>
        <button
          type="button"
          onClick={events.reload}
          className="px-3 py-1 text-sm border border-input rounded-lg hover:bg-muted/50"
        >
          Try again
        </button>
      </div>
    );
  }
  if (events.data.length === 0)
    return <p className="text-sm text-muted-foreground">No events yet. Add one on the Sales events page first.</p>;
  return (
    <ul className="space-y-2">
      {events.data.map((event) => (
        <li key={event.id}>
          <label className="flex items-start gap-3 p-3 border border-border rounded-lg cursor-pointer hover:bg-muted/50">
            <input
              type="checkbox"
              className="w-4 h-4 mt-1"
              checked={selected.includes(event.id)}
              onChange={() => onToggle(event.id)}
            />
            <span className="min-w-0">
              <span className="block text-base text-foreground break-words">{event.name}</span>
              <span className="block text-sm text-muted-foreground">
                {formatShortDate(`${event.starts_on}T12:00:00`)}
              </span>
            </span>
          </label>
        </li>
      ))}
    </ul>
  );
};

/** Step 1: who the campaign is for. Everything here only edits wizard state; the server checks it. */
const AudienceStep = ({ wizard }) => {
  const { state, patch, dispatch, lookups } = wizard;

  return (
    <div className="space-y-4">
      <div>
        <label htmlFor="wa-audience-source" className="block mb-1 text-sm text-foreground/80">
          Who should get this message?
        </label>
        <select
          id="wa-audience-source"
          aria-label="Audience source"
          value={state.source}
          onChange={(e) => dispatch({ type: 'source', source: e.target.value })}
          className={fieldClass}
        >
          {SOURCE_OPTIONS.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </select>
      </div>

      {state.source === SOURCE.WALK_IN_LEAD && (
        <div className="space-y-2">
          <p className="text-sm text-foreground/80">People who left their number at these events</p>
          <EventPicker
            events={lookups.events}
            selected={state.eventIds}
            onToggle={(id) => dispatch({ type: 'toggleEvent', id })}
          />
        </div>
      )}

      {state.source === SOURCE.CUSTOMER && (
        <div className="space-y-3">
          <div className="space-y-2">
            <p className="text-sm text-foreground/80">Storefront country (where the customer browsed from)</p>
            <label className="flex items-center gap-2 text-base text-foreground">
              <input
                type="checkbox"
                className="w-4 h-4"
                checked={state.anyCountry}
                onChange={(e) => patch({ anyCountry: e.target.checked })}
              />
              Any country
            </label>
            {!state.anyCountry && (
              <CountrySelect
                value={state.country}
                onChange={(iso) => patch({ country: iso })}
                ariaLabel="Storefront country"
              />
            )}
          </div>
          <div>
            <label htmlFor="wa-ordered-within" className="block mb-1 text-sm text-foreground/80">
              Ordered within
            </label>
            <select
              id="wa-ordered-within"
              aria-label="Ordered within"
              value={state.orderedWithin}
              onChange={(e) => patch({ orderedWithin: e.target.value })}
              className={fieldClass}
            >
              {ORDERED_WITHIN_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
          </div>
        </div>
      )}

      {state.source === SOURCE.ATELIER_CONTACT && (
        <p className="text-sm text-muted-foreground">
          Every trade account and prospect in the Atelier CRM that has a phone number.
        </p>
      )}

      {state.source === SOURCE.CSV && (
        <CsvAudienceInput
          text={state.csvText}
          country={state.csvCountry}
          onText={(csvText) => patch({ csvText })}
          onCountry={(csvCountry) => patch({ csvCountry })}
        />
      )}
    </div>
  );
};

export default AudienceStep;
